import { lazy, Suspense, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import { homePageLoader } from "./loaders/rootRouteLoader";
import { LoaderComponent } from "./PrivateRoute";
import RouteGuard from "./RouteGuard";
import ThemedOutletWrapper from "./ThemedOutletWrapper";
import withSuspense from "./withSuspense";
import FirstAccessibleRedirect from "./FirstAccessibleRouteIndex";
import { privateRoutes } from "./PrivateRoutes";
import {getWithLocalExpiry } from "@utils/index";

// Lazy imports
const LazySideDrawer = lazy(() => import("../component/drawer/Layout"));
const LazyNotFound = lazy(() => import("../screens/NotFound"));
const LazyLoginForm = lazy(() => import("../screens/login/LoginForm"));

const AuthRouter = () => {
  const {currentUser } = useSelector(
    (state) => state?.currentUser || {}
  );
  const dispatch = useDispatch()
  const roleId = currentUser?.roleId || null;
  
  const renderRoutes = (routes, isTopLevel = true) =>
    routes.map(({ path, component: Component, Permissions, children, index }) => ({
      index,
      path,
      ...(isTopLevel ? { loader:(v) =>homePageLoader({...v,dispatch}) } : {}),
      element: (
        <Suspense fallback={<LoaderComponent />}>
          <RouteGuard Permissions={Permissions} path={path}>
            <Component />
          </RouteGuard>
        </Suspense>
      ),
      children: children ? renderRoutes(children, false) : undefined,
    }));

  const router = useMemo(() => {
    const routes = [
      {
        element: <ThemedOutletWrapper />,
        children: [
          {
            path: "/permission-denieds",
            element: withSuspense(() => (
              <LazyNotFound
                code="503"
                title="Permission Denied"
                subtitle="Sorry, you don't have permission to access this page."
                enableBackBtn
              />
            )),
          },
          {
            path: "/",
            element: (
              <Suspense fallback={<LoaderComponent />}>
                {roleId && Boolean(getWithLocalExpiry()) ? <LazySideDrawer /> : <LazyLoginForm />}
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <FirstAccessibleRedirect key={roleId} />,
              },
              ...renderRoutes(privateRoutes, true),
              {
                path: "/permission-denied",
                element: withSuspense(() => (
                  <LazyNotFound
                    code="503"
                    title="Permission Denied"
                    subtitle="Sorry, you don't have permission to access this page."
                  />
                )),
              },
              { path: "*", element: withSuspense(() => <LazyNotFound />) },
            ],
          },
        ],
      },
    ];
    return createBrowserRouter(routes);
  }, [roleId]);

  return <RouterProvider router={router} key={roleId} />;
};

export default AuthRouter;
